// 创建axios实例
import axios from 'axios';
import { generate16DigitId, generateIdFromSceneList } from './uniqueIdGenerator';
import { logger } from './logger';
import type { SubtitleTemplate, SubtitleTemplatesResponse, VeoConfig, VeoGenerateParams, VeoTaskResponse, VeoTaskStatus } from '../types';

declare module 'axios' {
  interface InternalAxiosRequestConfig {
    skipAuth?: boolean;
  }
}

const API_BASE_URL = (import.meta as any).env?.VITE_API_BASE_URL;

// 创建axios实例并设置baseURL
const axiosInstance = axios.create({
  baseURL: API_BASE_URL
});

// 创建请求拦截器
axiosInstance.interceptors.request.use(
  (config) => {
    // 如果标记了 skipAuth，则不添加 Token
    if (config.skipAuth) {
      return config;
    }

    // 可以在这里添加token等认证信息
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    logger.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// 创建响应拦截器
axiosInstance.interceptors.response.use(
  (response) => {
    // 统一处理响应数据
    if (!response) {
      return Promise.reject({ message: 'No response received from server' });
    }
    return response.data;
  },
  (error) => {
    // 统一错误日志记录
    logger.error('API Error:', {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      message: error.message,
      data: error.response?.data
    });

    // 403错误处理：权益不足，跳转到会员购买页
    if (error.response?.status === 403 && window.location.pathname !== '/membership') {
      const errorMsg = error.response.data?.message || '权益不足，请前往会员中心购买会员';
      // 使用alert临时显示错误信息（后续可替换为Toast组件）
      alert(errorMsg);
      window.location.href = '/membership';
    }

    // 网络错误处理
    if (error.message.includes('Network Error')) {
      return Promise.reject({ message: '网络连接失败，请检查您的网络设置' });
    }

    // 超时错误处理
    if (error.code === 'ECONNABORTED') {
      return Promise.reject({ message: '请求超时，请稍后重试' });
    }

    // 确保返回的错误格式一致
    let errorData;
    if (error.response?.data) {
      errorData = error.response.data;
    } else if (error.message) {
      errorData = {
        message: error.message
      };
    } else {
      errorData = {
        message: '发生未知错误'
      };
    }

    return Promise.reject(errorData);
  }
);

// API请求函数类型定义
interface ApiResponse<T = any> {
  code: number;
  message: string;
  data: T;
}

// Gemini V2 支持的配置
interface SupportedConfigsResponseData {
  models: string[];
  aspect_ratios: Record<string, string[]>;
  image_sizes: Record<string, string[]>;
  input_image_limits: Record<string, number>;
}

// Gemini V2 模板接口
interface Template {
  id: string;
  name: string;
  description: string;
  type: string;
  is_system?: boolean;
  status: number;
  config: object;
  content_parts: Array<{
    type: string;
    required: boolean;
    description: string;
    position?: number;
  }>;
  extras?: object;
  version?: number;
  created_at?: string;
  updated_at?: string;
}

interface TemplatesResponseData {
  templates: Template[];
}

interface TemplateResponseData {
  template: Template;
}

// Gemini V2 图片生成接口
interface GeminiImageV2GenerateData {
  template_id?: string;
  prompt: string;
  negative_prompt?: string;
  image_size?: string;
  quality?: string;
  style?: string;
  num_images?: number;
  model?: string;
  reference_images?: string[];
  override_config?: object;
}

interface GeminiImageV2ResponseData {
  task_id: string;
  status: string;
  image_urls: string[];
  created_at: string;
  completed_at: string;
  error?: string;
}

// 用户注册接口
interface RegisterData {
  email: string;
  password: string;
  nickname?: string;
}

interface RegisterResponseData {
  user_id: number;
  nickname: string;
}

export const registerApi = async (data: RegisterData): Promise<ApiResponse<RegisterResponseData>> => {
  return axiosInstance.post('/auth/register', data);
};

// 用户登录接口
interface LoginData {
  email: string;
  password: string;
}

interface LoginResponseData {
  user_id: number;
  nickname: string;
  token: string;
}

// 用户登录接口
export const loginApi = async (data: LoginData): Promise<ApiResponse<LoginResponseData>> => {
  return axiosInstance.post('/auth/login', data);
};

// 发送密码重置邮件接口
interface ForgotPasswordData {
  email: string;
}

export const forgotPasswordApi = async (data: ForgotPasswordData): Promise<ApiResponse<null>> => {
  return axiosInstance.post('/auth/forgot-password', data);
};

// 重置密码接口
interface ResetPasswordData {
  email: string;
  token: string;
  new_password: string;
}

export const resetPasswordApi = async (data: ResetPasswordData): Promise<ApiResponse<null>> => {
  return axiosInstance.post('/auth/reset-password', data);
};

// Google登录接口
export const googleLoginApi = async (data: { id_token: string }): Promise<ApiResponse<LoginResponseData>> => {
  return axiosInstance.post('/auth/google-login', data);
};

// 用户登出接口
export const logoutApi = async (): Promise<ApiResponse<void>> => {
  return axiosInstance.post('/auth/logout');
};

// 获取支持的图片尺寸
interface ImageSizesResponseData {
  sizes: string[];
}

export const getImageSizes = async (): Promise<ApiResponse<ImageSizesResponseData>> => {
  return axiosInstance.get('/ai/image/sizes');
};

// 获取支持的音色列表
interface Voice {
  voice_id: string;
  name: string;
  gender: string;
  language: string;
  description: string;
  sample_rate: number;
  is_streaming: boolean;
}

interface VoicesResponseData {
  voices: Voice[];
  total: number;
}

export const getVoices = async (): Promise<ApiResponse<VoicesResponseData>> => {
  return axiosInstance.get('/ai/tts/voices');
};

// 图片生成接口
interface ImageGenerateData {
  prompt: string;
  negative_prompt?: string;
  image_urls?: string[];
  image_size: string;
  num_images: number;
  style?: string;
  quality?: string;
  seed?: number;
  parameters?: object;
  watermark?: boolean;
}

interface ImageGenerateResponseData {
  task_id: string;
  status: string;
  image_urls: string[];
}

export const generateImage = async (data: ImageGenerateData): Promise<ApiResponse<ImageGenerateResponseData>> => {
  return axiosInstance.post('/ai/image/generate', data);
};

// ==================== Gemini V2 图片生成接口 ====================

// 获取支持的配置（需要 Token）
export const getSupportedConfigs = async (): Promise<ApiResponse<SupportedConfigsResponseData>> => {
  return axiosInstance.get('/ai/image/v2/supported-configs');
};

// 生成图片（需要 Token）
export const generateImageV2 = async (
  data: GeminiImageV2GenerateData
): Promise<ApiResponse<GeminiImageV2ResponseData>> => {
  return axiosInstance.post('/ai/image/v2/generate', data);
};

// 模板分类接口
interface TemplateCategory {
  value: number;
  label: string;
}

interface TemplateCategoriesResponseData {
  categories: TemplateCategory[];
}

// 获取所有模板分类（需要 Token）
export const getTemplateCategories = async (): Promise<ApiResponse<TemplateCategoriesResponseData>> => {
  console.log('调用getTemplateCategories API');
  // 添加时间戳参数避免缓存
  const timestamp = new Date().getTime();
  return axiosInstance.get(`/canvas/templates/categories?timestamp=${timestamp}`);
};

// 获取所有可用模板（需要 Token）
export const getTemplates = async (category?: number): Promise<ApiResponse<TemplatesResponseData>> => {
  console.log('调用getTemplates API，分类:', category);
  // 添加时间戳参数避免缓存
  const timestamp = new Date().getTime();
  // 当category为-1时，不传递category参数，获取所有模板
  if (category === -1) {
    return axiosInstance.get('/canvas/templates', {
      params: { timestamp }
    });
  }
  return axiosInstance.get('/canvas/templates', {
    params: category !== undefined ? { category, timestamp } : { timestamp }
  });
};

// 获取指定模板的详细信息（需要 Token）
export const getTemplateById = async (id: string): Promise<ApiResponse<TemplateResponseData>> => {
  return axiosInstance.get(`/canvas/templates/${id}`);
};

// 创建新的模板（需要 Token）
export const createTemplate = async (data: Template): Promise<ApiResponse<TemplateResponseData>> => {
  return axiosInstance.post('/canvas/templates', data);
};

// 更新现有模板（需要 Token）
export const updateTemplate = async (id: string, data: Partial<Template>): Promise<ApiResponse<TemplateResponseData>> => {
  return axiosInstance.put(`/canvas/templates/${id}`, data);
};

// 获取指定模板的特定版本（需要 Token）
export const getTemplateVersion = async (id: string, version: number): Promise<ApiResponse<TemplateResponseData>> => {
  return axiosInstance.get(`/canvas/templates/${id}/versions/${version}`);
};

// 语音合成接口
interface TTSGenerateData {
  text: string;
  voice_type?: 'male' | 'female' | 'child';
  voice_name: string;
  audio_format?: 'mp3' | 'wav' | 'pcm';
  volume?: number;
  speed?: number;
  pitch?: number;
  emotion?: 'neutral' | 'happy' | 'sad' | 'angry' | 'fear';
  enable_subtitle?: boolean;
  subtitle_format?: 'srt' | 'vtt' | 'lrc';
  response_type?: 'url' | 'data';
}

interface TTSGenerateResponseData {
  task_id: string;
  status: string;
  audio_url?: string;
  audio_base64?: string;
  audio_format: string;
  duration: number;
  subtitle_url?: string;
  subtitle_format?: string;
  error?: string;
}

export const generateTTS = async (data: TTSGenerateData): Promise<ApiResponse<TTSGenerateResponseData>> => {
  return axiosInstance.post('/ai/tts/generate', data);
};

// 对话补全接口
interface ChatCompletionData {
  messages: Array<{ role: string; content: string }>;
  stream?: boolean;
  promptId?: string;
  model: string;
}

export const chatCompletion = async (data: ChatCompletionData) => {
  // 使用原生fetch实现流式请求，确保与原代码兼容
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  // 添加token到请求头
  const token = localStorage.getItem('token');
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE_URL}/ai/chat/completions`, {
    method: 'POST',
    headers,
    body: JSON.stringify(data)
  });

  // 403错误处理：权益不足，跳转到会员购买页
  if (response.status === 403 && window.location.pathname !== '/membership') {
    // 尝试读取错误信息
    let errorMsg = '权益不足，请前往会员中心购买会员';
    try {
      const errorData = await response.clone().json();
      if (errorData?.message) {
        errorMsg = errorData.message;
      }
    } catch (e) {
      // 解析失败时使用默认错误信息
    }
    alert(errorMsg);
    window.location.href = '/membership';
  }

  return response;
};

// 视频合成任务接口
interface VideoTaskRequestData {
  title: string;
  bgMusic?: {
    url: string | null;
    volume: number;
  };
  sceneList: Array<{
    id: string;
    desc: string;
    prompt?: string;
    dialogue: string;
    images?: string[];
    videoUrl: string;
    audioUrl: string;
    status: string;
    cursorPosition: {
      startTime: number;
      endTime: number;
    };
    transition?: {
      type: string;
      duration: number;
      direction?: string;
      easing?: string;
      color?: string;
    };
  }>;
  subtitleStyle?: any; // 字幕样式配置（可选）
}

interface VideoTaskResponseData {
  taskId: string;
}

export const createVideoTask = async (data: VideoTaskRequestData): Promise<ApiResponse<VideoTaskResponseData>> => {
  return axiosInstance.post('/video/tasks', data);
};

// 获取视频历史任务列表 - 根据API手册，应该使用获取指定类型项目接口
interface HistoryTasksParams {
  page?: number;
  pageSize?: number;
}

export const getHistoryTasks = async (params?: HistoryTasksParams): Promise<ApiResponse<any>> => {
  // 根据API手册，获取视频类型项目的接口是 /api/projects/type/:type
  return axiosInstance.get('/projects/type/video_project', { params });
};

// 删除视频历史任务 - 根据API手册，删除项目接口是 /api/projects/:id
export const deleteHistoryTask = async (taskId: string): Promise<ApiResponse<void>> => {
  // 根据API手册，删除项目的接口是 /api/projects/:id
  return axiosInstance.delete(`/projects/${taskId}`);
};

// 获取历史项目详情 - 根据API手册，获取项目详情的接口是 /api/projects/:id
export const getHistoryProject = async (projectId: string): Promise<ApiResponse<any>> => {
  // 根据API手册，获取项目详情的接口是 /api/projects/:id
  return axiosInstance.get(`/projects/${projectId}`);
};

// 保存场景列表到项目

interface SaveSceneListData {
  title: string;
  sceneList: any[]; // 更具体的类型定义需要根据实际场景结构调整
  bgMusic?: {
    url: string | null;
    volume: number;
  };
  voiceSpeed?: number;
  subtitleStyle?: any;  // 新增：字幕样式配置（可选）
  description?: string;
}

// 存储当前项目的 project_id，确保后续更新使用同一个 ID
let currentProjectId: string | null = null;

export const saveSceneList = async (data: SaveSceneListData): Promise<ApiResponse<any>> => {
  console.log('saveSceneList 函数被调用，参数：', data);

  // 为每个场景生成唯一的16位ID，如果已经存在则使用原有ID
  const sceneListWithUniqueIds = data.sceneList.map(scene => ({
    ...scene,
    id: scene.id || generate16DigitId() // 如果场景不存在id则生成，否则使用现有id
  }));

  // 🔧 关键修复：如果已有当前项目的 project_id，则复用；否则生成新的
  let project_id = currentProjectId;
  if (!project_id) {
    // 首次生成 project_id
    project_id = generateIdFromSceneList(sceneListWithUniqueIds);
    currentProjectId = project_id;
    console.log('🆔 [saveSceneList] 首次生成 project_id:', project_id);
  } else {
    console.log('🆔 [saveSceneList] 复用现有 project_id:', project_id);
  }

  // 准备项目配置参数
  const projectConfig: any = {
    bgMusic: data.bgMusic || {},
    voiceSpeed: data.voiceSpeed || 1.0,
    sceneList: sceneListWithUniqueIds
  };

  // 新增：如果有字幕样式，添加到 project_config
  if (data.subtitleStyle) {
    projectConfig.subtitleStyle = data.subtitleStyle;
  }

  const projectData = {
    type: 'video_project',
    title: data.title, // title为saveSceneListData的title
    project_config: JSON.stringify(projectConfig), // project_config内容为包含bgMusic、voiceSpeed、sceneList和subtitleStyle的对象作为字符串提交
    project_id: project_id // 使用保存的 project_id，确保更新时使用同一个
  };

  // 调用项目创建API（实际上这会创建或更新项目）
  return axiosInstance.post('/projects', projectData);
};

// 重置当前项目ID（用于新项目）或设置当前项目ID
export const resetCurrentProjectId = (projectId?: string) => {
  if (projectId) {
    console.log('🔄 [resetCurrentProjectId] 设置当前项目ID:', projectId);
    currentProjectId = projectId;
  } else {
    console.log('🔄 [resetCurrentProjectId] 重置当前项目ID');
    currentProjectId = null;
  }
};

// 反馈相关接口
interface FeedbackData {
  title: string;
  content: string;
  email: string;
}

export const submitFeedback = async (data: FeedbackData): Promise<ApiResponse<null>> => {
  return axiosInstance.post('/messages', data);
};

// 视频生成相关接口

// 视频生成请求参数
interface GenerateVideoParams {
  prompt: string;
  negative_prompt?: string;
  image_urls: string[];
  image_roles: string[];
  duration: number;
  aspect_ratio: string;
  resolution: string;
  fps: number;
  camera_movement?: string;
  style?: string;
  watermark?: boolean;
}

// 视频生成响应数据
interface GenerateVideoResponseData {
  task_id: string;
  status: string;
  video_url: string;
}

// 视频任务状态
interface VideoTaskStatusResponse {
  task_id: string;
  status: string;
  video_url?: string;
  error?: string;
}

// 视频生成接口
export const generateVideo = async (params: GenerateVideoParams): Promise<ApiResponse<GenerateVideoResponseData>> => {
  return axiosInstance.post('/ai/video/generate', params);
};

// 批量查询视频任务状态
export const batchGetVideoStatus = async (taskIds: string[]): Promise<ApiResponse<VideoTaskStatusResponse[]>> => {
  return axiosInstance.post('/ai/video/status/batch', { task_ids: taskIds });
};

// 获取支持的视频比例
export const getVideoAspectRatios = async (): Promise<ApiResponse<{ aspect_ratios: string[] }>> => {
  return axiosInstance.get('/ai/video/aspect-ratios');
};

// 获取支持的分辨率
export const getVideoResolutions = async (): Promise<ApiResponse<{ resolutions: string[] }>> => {
  return axiosInstance.get('/ai/video/resolutions');
};

// 获取支持的帧率
export const getVideoFps = async (): Promise<ApiResponse<{ fps: number[] }>> => {
  return axiosInstance.get('/ai/video/fps');
};

// 背景音乐相关接口
interface BgMusic {
  url: string;
  name: string;
  type: string;
  duration: number;
}

interface BgMusicListResponseData {
  musicList: BgMusic[];
}

// 获取背景音乐列表
export const getBgMusicList = async (): Promise<ApiResponse<BgMusicListResponseData>> => {
  return axiosInstance.get('/v1/bgmusic');
};

// ==================== 字幕相关接口 ====================

/**
 * 获取字幕模板列表
 * API路径: GET /api/subtitle/templates
 * @returns Promise<SubtitleTemplatesResponse>
 */
export const getSubtitleTemplates = async (): Promise<SubtitleTemplatesResponse> => {
  try {
    const response = await axiosInstance.get<SubtitleTemplatesResponse>('/subtitle/templates');
    return response.data || { templates: [] };
  } catch (error) {
    console.error('获取字幕模板失败，使用默认模板:', error);
    // 降级方案：返回硬编码的默认模板
    const defaultTemplates: SubtitleTemplate[] = [
      {
        id: 'default',
        name: '默认',
        category: 'basic',
        isDefault: true,
        style: {
          fontName: 'Arial',
          fontSize: 20,
          primaryColor: '#FFFFFF',
          outlineColor: '#000000',
          outlineWidth: 2,
          alignment: 'bottom',
          marginV: 20,
          bold: false,
          italic: false
        }
      },
      {
        id: 'cinematic',
        name: '电影感',
        category: 'cinematic',
        isDefault: false,
        style: {
          fontName: 'Arial',
          fontSize: 24,
          primaryColor: '#FFFF00',
          outlineColor: '#000000',
          outlineWidth: 3,
          alignment: 'bottom',
          marginV: 30,
          bold: true
        }
      },
      {
        id: 'simple',
        name: '简约',
        category: 'basic',
        isDefault: false,
        style: {
          fontName: 'Arial',
          fontSize: 18,
          primaryColor: '#FFFFFF',
          outlineColor: '#000000',
          outlineWidth: 1,
          alignment: 'bottom',
          marginV: 20
        }
      },
      {
        id: 'tech',
        name: '科技蓝',
        category: 'artistic',
        isDefault: false,
        style: {
          fontName: 'Arial',
          fontSize: 22,
          primaryColor: '#00BFFF',
          outlineColor: '#00008B',
          outlineWidth: 2,
          alignment: 'bottom',
          marginV: 25,
          bold: true
        }
      },
      {
        id: 'title',
        name: '标题突出',
        category: 'cinematic',
        isDefault: false,
        style: {
          fontName: 'Arial',
          fontSize: 32,
          primaryColor: '#FFD700',
          outlineColor: '#8B0000',
          outlineWidth: 4,
          alignment: 'center',
          marginV: 0,
          bold: true,
          backgroundColor: '#000000',
          backgroundOpacity: 0.5
        }
      }
    ];

    return { templates: defaultTemplates };
  }
};

// 文件上传接口
interface UploadFileResponseData {
  id: string;
  name: string;
  url: string;
  size: number;
  type: string;
  uploaded_at: string;
}

export const uploadFile = async (
  file: File,
  onProgress?: (progress: number) => void
): Promise<ApiResponse<UploadFileResponseData>> => {
  const formData = new FormData();
  formData.append('file', file);

  try {
    const response = await axiosInstance.post<ApiResponse<UploadFileResponseData>>(
      '/files/upload',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total && onProgress) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(progress);
          }
        }
      }
    );
    return response as ApiResponse<UploadFileResponseData>;
  } catch (error) {
    logger.error('文件上传失败:', error);
    throw error;
  }
};

// ==================== Veo API 接口 ====================

/**
 * 获取 Veo 配置信息
 * @returns Promise<ApiResponse<VeoConfig>>
 */
export const getVeoConfig = async (): Promise<ApiResponse<VeoConfig>> => {
  return axiosInstance.get('/ai/veo/config');
};

/**
 * 生成 Veo 视频
 * @param params 视频生成参数
 * @returns Promise<ApiResponse<VeoTaskResponse>>
 */
export const generateVeoVideo = async (params: VeoGenerateParams): Promise<ApiResponse<VeoTaskResponse>> => {
  return axiosInstance.post('/ai/veo/generate', params);
};

/**
 * 获取 Veo 任务状态
 * @param taskId 任务 ID
 * @returns Promise<ApiResponse<VeoTaskStatus>>
 */
export const getVeoTaskStatus = async (taskId: string): Promise<ApiResponse<VeoTaskStatus>> => {
  return axiosInstance.get('/ai/veo/task', {
    params: {
      task_id: taskId
    }
  });
};

/**
 * 取消 Veo 任务
 * @param taskId 任务 ID
 * @returns Promise<ApiResponse<any>>
 */
export const cancelVeoTask = async (taskId: string): Promise<ApiResponse<any>> => {
  return axiosInstance.delete('/ai/veo/task', {
    params: {
      task_id: taskId
    }
  });
};



export default axiosInstance;